import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { generalList2SpotService } from '../services/generalList2SpotService';
import { FlexibleDataGrid, type FlexibleDataGridColumn } from '../components/FlexibleDataGrid';

type SpotRow = {
  id: string;
  rowKey: string;
  archiveCode: string;
  folderNumber: string;
  evrakSiraNo: string;
  invoiceNumber: string;
  sevkKodu: string;
  planKodu: string;
  dorse: string;
  surucuAdi: string;
  surucuKonumu: string;
  surucuTelefon: string;
  surucuGunlukSureKalani: string;
  surucuHaftalikSureKalani: string;
  surucuIkiHaftalikSureKalani: string;
  tcKimlikNo: string;
  istenenKasaTipi: string;
  durum: string;
  islemDurum: string;
  accountingStatus: string;
  operasyonNot: string;
  yuklemeTarihi: string;
  aracAtamaTarihi: string;
  updatedAt: string;
  cekici: string;
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const normalizePhone = (value?: string) => {
  if (!value) return '-';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '-';
  return digits;
};

const mapToSpotRow = (item: any, index: number): SpotRow => ({
  id: String(item.id || ''),
  rowKey: [String(item.id || ''), String(item.sevk_kodu || item.shipment_no || ''), String(index)].join('::'),
  archiveCode: item.archive_code || item.archiveCode || '-',
  folderNumber: item.folder_number ? String(item.folder_number) : item.folderNumber ? String(item.folderNumber) : '-',
  evrakSiraNo: item.evrak_sira_no ? String(item.evrak_sira_no) : item.folder_number ? String(item.folder_number) : '-',
  invoiceNumber: item.fatura_no || item.invoice_no || item.invoiceNumber || '-',
  sevkKodu: item.sevk_kodu || item.shipment_no || '-',
  planKodu: item.plan_kodu || item.plan_code || '-',
  cekici: item.cekici || item.cekici_plaka || item.arac_plaka || '-',
  dorse: item.dorse_plaka || item.assigned_trailer_plate || '-',
  surucuAdi: item.surucu_bilgileri || item.driver_name || item.assigned_driver_name || '-',
  surucuKonumu: item.current_address || item.currentAddress || '-',
  surucuTelefon: normalizePhone(item.surucu_telefon || item.driver_phone || item.assigned_driver_phone || item.fallback_driver_phone),
  surucuGunlukSureKalani: item.surucu_gunluk_sure_kalani || item.available_drive_period || '-',
  surucuHaftalikSureKalani: item.surucu_haftalik_sure_kalani || '-',
  surucuIkiHaftalikSureKalani: item.surucu_iki_haftalik_sure_kalani || '-',
  tcKimlikNo: item.tc_kimlik_no || item.tc_number || '-',
  istenenKasaTipi: item.arac_turu || item.vehicle_spec_type || item.aracTipi || item.vehicle_type || '-',
  durum: item.durum || item.status || item.process_status || '-',
  islemDurum: item.islem_durum || item.process_status || item.durum || item.status || '-',
  accountingStatus: item.accounting_status || 'none',
  operasyonNot: String(item.operasyon_not || item.aciklama || item.notlar || item.notes || '').split(' | ').filter((p: string) => p && !/^(Sipariş|Termin|Yükleme Ara|Boşaltma Ara)/.test(p)).join(' | ').trim() || '-',
  yuklemeTarihi: item.yukleme_talep_tarihi || item.pickup_date || '',
  aracAtamaTarihi: item.arac_atama_tarihi || item.assigned_at || '',
  updatedAt: item.updated_at || item.updatedAt || '',
});

const ACCOUNTING_BADGE: Record<string, string> = {
  'none': 'bg-slate-100 text-slate-600',
  'invoice-approval-pending': 'bg-amber-50 text-amber-700 border border-amber-200',
  'payment-pending': 'bg-blue-50 text-blue-700 border border-blue-200',
  'payment-completed': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
};

const columns: FlexibleDataGridColumn<SpotRow>[] = [
  { key: 'sevkKodu', label: 'Sipariş #', width: 130, sortable: true },
  { key: 'planKodu', label: 'Plan Kodu', width: 130, sortable: true },
  { key: 'surucuAdi', label: 'Sürücü', width: 180, sortable: true, visibleByDefault: true },
  { key: 'cekici', label: 'Çekici', width: 120 },
  { key: 'dorse', label: 'Dorse', width: 120 },
  { key: 'surucuKonumu', label: 'Sürücü Konumu', width: 260 },
  { key: 'surucuTelefon', label: 'Sürücü Telefonu', width: 160 },
  { key: 'surucuGunlukSureKalani', label: 'Günlük Süre', width: 130 },
  { key: 'surucuHaftalikSureKalani', label: 'Haftalık Süre', width: 140 },
  { key: 'surucuIkiHaftalikSureKalani', label: '2 Haftalık Süre', width: 150 },
  { key: 'tcKimlikNo', label: 'TC Kimlik', width: 140 },
  { key: 'istenenKasaTipi', label: 'Kasa Tipi', width: 140 },
  { key: 'durum', label: 'Durum', width: 170, sortable: true },
  { key: 'islemDurum', label: 'İşlem Durum', width: 170, sortable: true },
  { key: 'accountingStatus', label: 'Muhasebe', width: 140, render: (row) => {
    const status = row.accountingStatus || 'none';
    const label = status === 'none' ? 'Yok' : status === 'invoice-approval-pending' ? 'Fatura Onay Bekliyor' : status === 'payment-pending' ? 'Ödeme Bekliyor' : status === 'payment-completed' ? 'Ödendi' : status;
    const cls = ACCOUNTING_BADGE[status] || ACCOUNTING_BADGE['none'];
    return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>;
  }},
  { key: 'operasyonNot', label: 'Not', width: 260 },
  { key: 'archiveCode', label: 'Arşiv No', width: 100 },
  { key: 'folderNumber', label: 'Klasör', width: 100 },
  { key: 'invoiceNumber', label: 'Fatura No', width: 140 },
  { key: 'yuklemeTarihi', label: 'Yükleme Tarihi', width: 140, getValue: (row) => row.yuklemeTarihi, render: (row) => formatDateTime(row.yuklemeTarihi) },
  { key: 'aracAtamaTarihi', label: 'Atama Tarihi', width: 140, getValue: (row) => row.aracAtamaTarihi, render: (row) => formatDateTime(row.aracAtamaTarihi) },
  { key: 'updatedAt', label: 'Güncelleme', width: 140, getValue: (row) => row.updatedAt, render: (row) => formatDateTime(row.updatedAt) },
];

export function GeneralList2Spot() {
  const [rows, setRows] = useState<SpotRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await generalList2SpotService.getAll();
        if (cancelled) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setRows(list.map((item: any, index: number) => mapToSpotRow(item, index)));
      } catch {
        if (!cancelled) toast.error('Spot sürücü verileri alınamadı');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="p-4">
      <FlexibleDataGrid
        title="Spot Durum Listesi"
        rows={rows}
        columns={columns}
        getRowKey={(row) => row.rowKey}
        loading={loading}
        emptyMessage="Aktif spot sürücü bulunamadı."
        searchable
        selectable
        initialPageSize={50}
        pageSizeOptions={[25, 50, 100, 200]}
      />
    </div>
  );
}
